import React, { useState, useEffect } from 'react';
import {
  Card, Button, Modal, Form, Input, Select, Space, Tag,
  Row, Col, Statistic, Typography, Calendar, Badge,
  Popconfirm, Alert, List,
  InputNumber,
} from 'antd';
import {
  PlusOutlined, CalendarOutlined, CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { useAuth } from '../../../context/UnifiedAuthContext';
import { Amenity, AmenityBooking as AmenityBookingType } from '../types';
import { staticApi } from '../utils/staticApi';
import { isOwnRow, resolveOwnerRecordKey } from '../utils/resolveOwnerKeys';

dayjs.extend(isSameOrAfter);

const { Title, Text } = Typography;
const { Option } = Select;

type BookingStatus = 'pending' | 'confirmed' | 'cancelled';

const STATUS_CONFIG: Record<BookingStatus, { color: string; label: string }> = {
  pending:   { color: 'gold',  label: 'Pending Approval' },
  confirmed: { color: 'green', label: 'Confirmed' },
  cancelled: { color: 'red',   label: 'Cancelled' },
};

export const AmenityBooking: React.FC = () => {
  const {
    owners,
    amenities,
    amenityBookings: bookings,
    fetchAmenities,
    fetchAmenityBookings,
    createAmenityBooking,
    updateAmenityBooking,
    deleteAmenityBooking,
    approveAmenityBooking,
    cancelAmenityBooking,
  } = useCondoStore();
  const { currentCondo, user: storeUser } = useAuthStore();
  const { user } = useAuth();
  const isAdminRole = user?.role === 'super_admin_condo' || user?.role === 'superadmin' || user?.role === 'admin';
  const isOwnerRole = user?.role === 'owner';

  // QA W1 — authorization: only admin/manager/super-admin may manage (approve /
  // delete) bookings, and only they may cancel OTHERS' bookings. An owner may
  // still cancel THEIR OWN booking.
  //
  // Sprint 136 (ADR-131, R12-6) — this used to resolve the owners-table NUMERIC
  // id and compare it to `b.ownerId`, which the server stamps with the login-user
  // UUID. `"a0000000-…" === 51` is never true, so the check didn't gate owners to
  // their own rows — it gated EVERY row, including the owner's own, and no Cancel
  // button ever rendered. Compare on the canonical user key instead; `isOwnRow`
  // is the same seam the Atrium owner home matches rows with.
  const isMyBooking = (b: AmenityBookingType) =>
    isOwnerRole && isOwnRow(b.ownerId, user);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState<Amenity | null>(null);
  const [, setSelectedDate] = useState<Dayjs>(dayjs());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [editing, setEditing] = useState<AmenityBookingType | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAmenities(currentCondo?.id);
    fetchAmenityBookings(currentCondo?.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetch actions are stable zustand actions
  }, [currentCondo?.id]);

  // Conflict detection
  const hasConflict = (amenityId: string, date: string, start: string, end: string, excludeId?: string): boolean => {
    return bookings
      .filter(b => b.id !== excludeId && b.amenityId === amenityId && b.date === date && b.status !== 'cancelled')
      .some(b => start < b.endTime && end > b.startTime);
  };

  const handleOpenBooking = (amenity?: Amenity, date?: Dayjs) => {
    const am = amenity ?? amenities[0];
    if (!am) { staticApi.message.warning('No amenities available yet.'); return; }
    setSelectedAmenity(am);
    form.resetFields();

    // Pre-populate owner's own info when booking as a resident
    const ownerRecord = isOwnerRole
      ? owners.find(o => isOwnRow(o.userId, user) || o.id === storeUser?.ownerId)
      : undefined;

    form.setFieldsValue({
      amenityId: am.id,
      date: (date ?? dayjs()).format('YYYY-MM-DD'),
      startTime: '09:00',
      endTime: '11:00',
      attendees: 1,
      status: 'pending', // owners always go through approval
      ...(ownerRecord && {
        // Sprint 136 — the canonical user key, NOT `ownerRecord.id`. This value
        // is persisted verbatim as the booking's owner_id (see handleSubmit).
        ownerId: resolveOwnerRecordKey(ownerRecord),
        unitNumber: ownerRecord.unitNumber,
        ownerName: ownerRecord.name,
      }),
    });
    setEditing(null);
    setModalOpen(true);
  };

  // Sprint 136 — the Select's value is the CANONICAL owner key (see the options
  // below), so look the record back up by that key, not by the numeric id.
  const handleOwnerSelect = (ownerId: string) => {
    const owner = owners.find(o => resolveOwnerRecordKey(o) === ownerId);
    if (owner) form.setFieldsValue({ unitNumber: owner.unitNumber, ownerName: owner.name });
  };

  const handleAmenitySelect = (amenityId: string) => {
    setSelectedAmenity(amenities.find(a => a.id === amenityId) ?? null);
  };

  const calcFee = (amenityId: string, start: string, end: string): number => {
    const am = amenities.find(a => a.id === amenityId);
    if (!am || am.feePerHour === 0) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const hours = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
    return Math.max(0, hours) * am.feePerHour;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const am = amenities.find(a => a.id === values.amenityId);
      if (!am) return;

      if (hasConflict(values.amenityId, values.date, values.startTime, values.endTime, editing?.id)) {
        staticApi.message.error('This amenity is already booked for the selected time slot. Please choose a different time.');
        return;
      }

      if (values.attendees > am.capacity) {
        staticApi.message.error(`${am.name} has a maximum capacity of ${am.capacity} people.`);
        return;
      }

      const fee = calcFee(values.amenityId, values.startTime, values.endTime);

      if (editing) {
        await updateAmenityBooking(editing.id, {
          ...values,
          amenityName: am.name,
          totalFee: fee,
          status: values.status?.toUpperCase(),
        });
        staticApi.message.success('Booking updated');
      } else {
        await createAmenityBooking({
          amenityId: values.amenityId,
          amenityName: am.name,
          condoId: currentCondo?.id || '',
          unitNumber: values.unitNumber,
          ownerName: values.ownerName,
          ownerId: values.ownerId ?? '',
          date: values.date,
          startTime: values.startTime,
          endTime: values.endTime,
          attendees: values.attendees,
          purpose: values.purpose,
          status: values.status?.toUpperCase(),
          totalFee: fee,
          createdAt: dayjs().toISOString(),
          notes: values.notes,
        });
        staticApi.message.success(`${am.name} booked ${am.requiresApproval ? '— pending approval' : 'and confirmed!'}`);
      }
      setModalOpen(false);
    } catch { /* validation */ }
  };

  const handleApprove = async (b: AmenityBookingType) => {
    try {
      await approveAmenityBooking(b.id);
      staticApi.message.success('Booking approved');
    } catch {
      staticApi.message.error('Failed to approve booking');
    }
  };

  const handleCancel = async (b: AmenityBookingType) => {
    try {
      await cancelAmenityBooking(b.id);
      staticApi.message.info('Booking cancelled');
    } catch {
      staticApi.message.error('Failed to cancel booking');
    }
  };

  // Calendar cell renderer
  const getDateBookings = (date: Dayjs) => {
    return bookings.filter(b => b.date === date.format('YYYY-MM-DD') && b.status !== 'cancelled');
  };

  const dateCellRender = (date: Dayjs) => {
    const dayBookings = getDateBookings(date);
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {dayBookings.slice(0, 3).map(b => (
          <li key={b.id}>
            <Badge
              color={amenities.find(a => a.id === b.amenityId)?.color ?? '#1890ff'}
              text={<span style={{ fontSize: 11 }}>{b.startTime} {b.amenityName}</span>}
            />
          </li>
        ))}
        {dayBookings.length > 3 && <li><Text type="secondary" style={{ fontSize: 11 }}>+{dayBookings.length - 3} more</Text></li>}
      </ul>
    );
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const todayBookings = bookings.filter(b => b.date === dayjs().format('YYYY-MM-DD') && b.status !== 'cancelled');

  // Sprint 144 (R14-23, ADR-139) — a resident was shown the MANAGER's framing
  // ("Manage reservations for all common areas and amenities") and
  // building-wide counters: "Total Bookings" counted every unit's reservations
  // and "Pending Approval" counted a queue only management can act on. Neither
  // is a resident's number. Residents now get their own copy and tiles scoped
  // to THEIR OWN bookings. The calendar/list below stays building-wide on
  // purpose — seeing when a shared amenity is taken is the point of the page,
  // and Sprint 136's redacted booking DTO is what keeps other units' details
  // out of it.
  const myBookings   = bookings.filter(isMyBooking);
  const myUpcoming   = myBookings.filter(b =>
    b.status !== 'cancelled' && b.date >= dayjs().format('YYYY-MM-DD'));
  const myPending    = myBookings.filter(b => b.status === 'pending');

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          <CalendarOutlined style={{ marginRight: 10, color: '#13c2c2' }} />
          {isOwnerRole ? 'Book an Amenity' : 'Amenity Booking'}
        </Title>
        <Text type="secondary">
          {isOwnerRole
            ? 'Reserve a common area for your household and track your own reservations.'
            : 'Manage reservations for all common areas and amenities'}
        </Text>
      </div>

      <Row gutter={16} style={{ marginBottom: 20 }}>
        {isOwnerRole ? (
          <>
            <Col span={6}><Card><Statistic title="My Bookings" value={myBookings.length} prefix={<CalendarOutlined />} /></Card></Col>
            <Col span={6}><Card><Statistic title="My Upcoming" value={myUpcoming.length} valueStyle={{ color: '#1890ff' }} /></Card></Col>
            <Col span={6}><Card><Statistic title="Awaiting Approval" value={myPending.length} valueStyle={{ color: myPending.length > 0 ? '#fa8c16' : '#52c41a' }} /></Card></Col>
          </>
        ) : (
          <>
            <Col span={6}><Card><Statistic title="Total Bookings" value={bookings.length} prefix={<CalendarOutlined />} /></Card></Col>
            <Col span={6}><Card><Statistic title="Today's Bookings" value={todayBookings.length} valueStyle={{ color: '#1890ff' }} /></Card></Col>
            <Col span={6}><Card><Statistic title="Pending Approval" value={pendingBookings.length} valueStyle={{ color: pendingBookings.length > 0 ? '#fa8c16' : '#52c41a' }} /></Card></Col>
          </>
        )}
        <Col span={6}><Card><Statistic title="Amenities Available" value={amenities.length} prefix={<EnvironmentOutlined />} /></Card></Col>
      </Row>

      {/* Pending Approvals — visible to admins only */}
      {isAdminRole && pendingBookings.length > 0 && (
        <Alert
          message={`${pendingBookings.length} booking(s) awaiting your approval`}
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={() => setViewMode('list')}>View All</Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={16}>
        {/* Amenity Cards */}
        <Col span={6}>
          <Card title="Amenities" size="small" style={{ marginBottom: 16 }}>
            <List
              dataSource={amenities}
              renderItem={am => (
                <List.Item
                  key={am.id}
                  style={{ cursor: 'pointer', padding: '8px 0' }}
                  actions={[
                    <Button size="small" type="link" icon={<PlusOutlined />} onClick={() => handleOpenBooking(am)}>Book</Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Badge color={am.color} />}
                    title={<Text strong style={{ fontSize: 13 }}>{am.name}</Text>}
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary" style={{ fontSize: 11 }}>{am.location} · Cap: {am.capacity}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>{am.feePerHour > 0 ? `$${am.feePerHour}/hr` : 'Free'}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Calendar / List */}
        <Col span={18}>
          <Card
            title="Booking Schedule"
            extra={
              <Space>
                <Button.Group>
                  <Button type={viewMode === 'calendar' ? 'primary' : 'default'} onClick={() => setViewMode('calendar')}>Calendar</Button>
                  <Button type={viewMode === 'list' ? 'primary' : 'default'} onClick={() => setViewMode('list')}>List</Button>
                </Button.Group>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenBooking()}>New Booking</Button>
              </Space>
            }
          >
            {viewMode === 'calendar' ? (
              <Calendar
                cellRender={dateCellRender}
                onSelect={date => setSelectedDate(date)}
              />
            ) : (
              <List
                dataSource={[...bookings].sort((a, b) => a.date.localeCompare(b.date))}
                renderItem={b => (
                  <List.Item
                    key={b.id}
                    actions={[
                      // QA W1 — Approve is a management action: admin/manager+ only.
                      isAdminRole && b.status === 'pending' && <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => handleApprove(b)}>Approve</Button>,
                      // QA W1 — Cancel allowed for admins on any booking, or for
                      // an owner on THEIR OWN booking only.
                      b.status !== 'cancelled' && (isAdminRole || isMyBooking(b)) && <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => handleCancel(b)}>Cancel</Button>,
                      // QA W1 — Delete is a management action: admin/manager+ only.
                      isAdminRole && (
                        <Popconfirm title="Delete?" onConfirm={() => deleteAmenityBooking(b.id)}>
                          <Button size="small" icon={<DeleteOutlined />} />
                        </Popconfirm>
                      ),
                    ].filter(Boolean)}
                  >
                    <List.Item.Meta
                      avatar={<Badge color={amenities.find(a => a.id === b.amenityId)?.color ?? '#1890ff'} />}
                      title={
                        <Space>
                          <Text strong>{b.amenityName}</Text>
                          <Tag color={STATUS_CONFIG[b.status].color}>{STATUS_CONFIG[b.status].label}</Tag>
                        </Space>
                      }
                      description={
                        <Space>
                          <Text>{dayjs(b.date).format('MMM D, YYYY')}</Text>
                          <Text type="secondary">{b.startTime} – {b.endTime}</Text>
                          {/* Sprint 136 — a redacted row (another resident's, seen
                              by an owner) carries no unit/name: the slot still
                              shows as busy, but says only "Reserved". */}
                          {b.ownerName
                            ? <Text>Unit {b.unitNumber} · {b.ownerName}</Text>
                            : <Text type="secondary">Reserved</Text>}
                          {b.totalFee != null && b.totalFee > 0 && <Tag color="gold">${b.totalFee.toFixed(2)}</Tag>}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
                locale={{ emptyText: 'No bookings yet. Click "New Booking" to get started.' }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Booking Modal */}
      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title={editing ? 'Edit Booking' : 'New Amenity Booking'}
        onOk={handleSubmit}
        width={620}
        okText={editing ? 'Save Changes' : 'Book Now'}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={isOwnerRole ? 24 : 12}>
              <Form.Item name="amenityId" label="Amenity" rules={[{ required: true }]}>
                <Select onChange={handleAmenitySelect} optionLabelProp="label">
                  {amenities.map(a => (
                    <Option key={a.id} value={a.id} label={`${a.name} (Cap: ${a.capacity})`}>
                      <Badge color={a.color} text={`${a.name} (Cap: ${a.capacity})`} />
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            {!isOwnerRole && (
              <Col span={12}>
                <Form.Item name="ownerId" label="Resident">
                  {/* Sprint 136 — value MUST be the canonical owner key: this
                      field is persisted as the booking's owner_id, and a numeric
                      owners-table id there is unmatchable by the server's
                      own-booking check. Mirrors ManualPaymentForm / FeeFormModal. */}
                  <Select showSearch placeholder="Select resident" optionFilterProp="label" onChange={handleOwnerSelect}
                    options={owners.map(o => ({ label: `Unit ${o.unitNumber} — ${o.name}`, value: resolveOwnerRecordKey(o) }))} />
                </Form.Item>
              </Col>
            )}
          </Row>
          {/* Sprint 141 (R13-4) — read-only. The server DERIVES both from the
              resolved owner record on create and update (Sprint 136 / ADR-131,
              `applyOwnerIdentity`) and discards whatever the client sends: a
              Round-12 probe submitted `Unit 999 / "Someone Else McSpoof"` and the
              row persisted as the caller's real unit and name. Leaving them
              editable was therefore a control that appears to do something and
              doesn't — the same trust smell as R11-1's phantom Credit Card and
              R12-1's discarded owner+method. They stay VISIBLE because confirming
              who a booking is for is genuinely useful; they just no longer invite
              an edit that will be thrown away. */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="unitNumber" label="Unit Number" rules={[{ required: true }]}>
                <Input readOnly disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ownerName"
                label="Resident Name"
                rules={[{ required: true }]}
                extra="Set from the resident's record."
              >
                <Input readOnly disabled />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="date" label="Date" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="startTime" label="Start Time" rules={[{ required: true }]}><Input type="time" /></Form.Item></Col>
            <Col span={8}><Form.Item name="endTime" label="End Time" rules={[{ required: true }]}><Input type="time" /></Form.Item></Col>
          </Row>
          {/* Sprint 144 (R14-22, ADR-139) — the Status select is MANAGER-ONLY.
              A resident could pick "Confirmed" and the server correctly forced
              the row to PENDING, so the control was misleading rather than a
              hole — the same decorative-field shape as R13-4's unit/name pair
              above. Residents no longer see it; the form still carries the
              hidden `status: 'pending'` default set in handleOpenBooking, so
              the submitted payload is unchanged. Managers keep the control
              because for them it genuinely takes effect. */}
          <Row gutter={16}>
            <Col span={isOwnerRole ? 24 : 12}>
              <Form.Item name="attendees" label="Number of Attendees" rules={[{ required: true }]}>
                <InputNumber min={1} max={selectedAmenity?.capacity ?? 100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            {!isOwnerRole && (
              <Col span={12}>
                <Form.Item name="status" label="Status">
                  <Select>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
                  </Select>
                </Form.Item>
              </Col>
            )}
          </Row>
          <Form.Item name="purpose" label="Purpose / Event Description" rules={[{ required: true }]}>
            <Input.TextArea rows={2} placeholder="e.g. Birthday party, board meeting, family gathering…" />
          </Form.Item>
          <Form.Item name="notes" label="Additional Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          {selectedAmenity && selectedAmenity.requiresApproval && (
            <Alert message="This amenity requires management approval. You will be notified once the booking is confirmed." type="info" showIcon />
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default AmenityBooking;
